import React, { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Divider from "@mui/material/Divider";
import MyLocationIcon from "@mui/icons-material/MyLocation";
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import WavingHandIcon from '@mui/icons-material/WavingHand';
import { yellow, green, red } from '@mui/material/colors';
import { Button, TextField } from "@mui/material";
import { IconButton, InputAdornment, CircularProgress } from "@mui/material";
import {
  Box,
  Paper,
  Typography,
  createTheme,
  Chip,
  FormControlLabel,
  Switch,
} from "@mui/material";

import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import { useTheme } from "@mui/material/styles";


import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";


import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ComplexStatisticsCard from "examples/Cards/StatisticsCards/ComplexStatisticsCard";
import ReportsBarChart from "examples/Charts/BarCharts/ReportsBarChart";
import ReportsLineChart from "examples/Charts/LineCharts/ReportsLineChart";
import LogoutButton from "layouts/logout";
import DropDown from "./admin_dashboard";
import api from "core/interceptors/api";
import { disableBackNavigation } from "core/utils/storage";

function Dashboard() {
  const navigate = useNavigate();
  const theme = useTheme();

  const role = localStorage.getItem("role");
  const email = localStorage.getItem("email");

  const [vendorEmail, setVendorEmail] = useState(null);
  const [isAvailable, setIsAvailable] = useState(true);
  const [isLoading, setIsLoading] = useState(true);

  const [dashboard, setDashboard] = useState({
    order_count: 0,
    pending_count: 0,
    sales_count: 0,
    reject_count: 0,
    product_count: 0,
    customer_count: 0,
    vendor_count: 0,
  });
  const [currentLocation, setCurrentLocation] = useState("");
  const [locationCoords, setLocationCoords] = useState({ latitude: '', longitude: '' });
  const [isFetchingLocation, setIsFetchingLocation] = useState(false);
  const [chartDataWeek, setChartDataWeek] = useState(null);
  const [chartDataToday, setChartDataToday] = useState(null);

  const [chartDataMonth, setChartMonthData] = useState({
    labels: [],
    datasets: [{ label: "Sales", data: [] }],
  });

  const allowedRoles = ["Admin", "Super Admin", "Marketing Manager", "Vendor", "Telecaller"];

  const [selectedEmail, setSelectedEmail] = useState(() => {
    return !allowedRoles.includes(role) ? email : "";
  });



  useEffect(() => {
    disableBackNavigation();
  }, []);


  useEffect(() => {
    if (role === "Services") {
      const storedEmail = localStorage.getItem("email");
      if (!storedEmail) {
        Swal.fire("Error", "Vendor email not found. Please log in again.", "error");
        setIsLoading(false);
        return;
      }

      setVendorEmail(storedEmail);

      const fetchStatus = async () => {
        setIsLoading(true);
        try {
          const response = await api.get(`services_register/${storedEmail}/status`);
          if (response.data && typeof response.data.available === "boolean") {
            setIsAvailable(response.data.available);
          }
        } catch (error) {
          console.error("Failed to fetch vendor status:", error);
          Swal.fire("Error", "Could not fetch your current status.", "error");
        } finally {
          setIsLoading(false);
        }
      };

      fetchStatus();
    }
  }, [role]);


  useEffect(() => {
    if (!role) return;

    const allowedRoles = ["Admin", "Super Admin", "Marketing Manager", "Vendor", "Telecaller"];

    const shouldFetch =
      allowedRoles.includes(role) ||
      (selectedEmail && !allowedRoles.includes(role));


    if (shouldFetch) {
      fetchOrders(selectedEmail);
      fetchDailyAndWeeklyChartData(selectedEmail);
      fetchMonthlyChartData(selectedEmail);
    }
  }, [selectedEmail, role]);

  const handleAvailabilityChange = async (event) => {
    const newStatus = event.target.checked;
    const previousStatus = isAvailable;

    setIsAvailable(newStatus);

    try {
      const response = await api.put(`services_register/${vendorEmail}/availability`, {
        available: newStatus,
      });

      Swal.fire({
        icon: "success",
        title: "Status Updated!",
        text: response.data.message || "Your availability has been changed.",
        timer: 2000,
        showConfirmButton: false,
        toast: true,
        position: "bottom-end",
      });
    } catch (error) {
      setIsAvailable(previousStatus);
      console.error("Failed to update status:", error);
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text: error.response?.data?.error || "Could not update your status.",
      });
    }
  };


  const handleGetCurrentLocation = useCallback(() => {
    setIsFetchingLocation(true);

    if (!navigator.geolocation) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Geolocation not supported by your browser.",
      });
      setIsFetchingLocation(false);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const controller = new AbortController();
        const signal = controller.signal;

        try {
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&email=a.malarpriya@gmail.com`,
            {
              method: "GET",
              headers: {
                "Accept-Language": "en",
                "User-Agent": "justclickindia/1.0 (a.malarpriya@gmail.com)",
              },
              signal,
            }
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          const placeName = data.display_name || "Location not found";

          setCurrentLocation(placeName);
          setLocationCoords({ latitude, longitude });

          // Save location to backend
          await api.put(`/services_register/${vendorEmail}/current_location`, {
            current_location: placeName,
          });

          Swal.fire({
            icon: "success",
            title: "Location updated!",
            showConfirmButton: false,
            timer: 1600,
            toast: true,
            position: "bottom-end",
          });

        } catch (error) {
          if (error.name === "AbortError") return;

          console.error("Error fetching or saving location:", error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to fetch or save location.",
          });
        } finally {
          setIsFetchingLocation(false);
        }
      },
      (error) => {
        console.error("Error fetching location:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.message || "Permission denied or location unavailable.",
        });
        setIsFetchingLocation(false);
      },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 }
    );
  }, [vendorEmail, setCurrentLocation, setLocationCoords]);



  // Load saved location on vendorEmail set
  useEffect(() => {
    if (vendorEmail) {
      const fetchSavedLocation = async () => {
        try {
          const res = await api.get(`/services_register/${vendorEmail}/current_location`);
          if (res.data?.current_location) {
            setCurrentLocation(res.data.current_location);
          }
        } catch (err) {
          console.error("Could not fetch saved location", err);
        }
      };
      fetchSavedLocation();
    }
  }, [vendorEmail]);

  const fetchOrders = async (emailParam) => {
    const params =
      (role === "Admin" || role === "Super Admin" || role === "Marketing Manager" || role === "Telecaller") && !emailParam ? {} : { email: localStorage.getItem("email") };

    try {
      const res = await api.get("/dashboard/fetch", { params });
      setDashboard(res.data || dashboard);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  const fetchDailyAndWeeklyChartData = async (emailParam) => {
    try {
      const response = await api.get("/dashboard/fetch", {
        params: { email: emailParam },
      });

      const dailyOrders = response.data?.daily_orders || [];

      const weeklyLabels = dailyOrders.map((order) => order.day);
      const weeklyData = dailyOrders.map((order) => order.count);
      setChartDataWeek({
        labels: weeklyLabels,
        datasets: { label: "Sales", data: weeklyData },
      });

      const today = new Date();
      const todayFormatted = `${today.getFullYear()}-${(today.getMonth() + 1)
        .toString()
        .padStart(2, "0")}-${today.getDate().toString().padStart(2, "0")}`;

      const todayData = dailyOrders.find((order) => order.day === todayFormatted);

      if (todayData) {
        const todayDayName = new Date(todayData.day).toLocaleDateString("en-US", {
          weekday: "long",
        });
        setChartDataToday({
          labels: [todayDayName],
          datasets: { label: "Sales", data: [todayData.count] },
        });
      } else {
        setChartDataToday({
          labels: [],
          datasets: { label: "Sales", data: [] },
        });
      }
    } catch (error) {
      console.error("Error fetching daily and weekly chart data:", error);
    }
  };

  const fetchMonthlyChartData = async (emailParam) => {
    try {
      const response = await api.get("/dashboard/fetch", {
        params: { email: emailParam },
      });

      const monthlyOrders = response.data?.monthly_orders || [];
      const allMonthsData = new Array(12).fill(0);
      monthlyOrders.forEach((order) => {
        allMonthsData[order.month - 1] = order.count;
      });

      setChartMonthData({
        labels: [
          "Jan",
          "Feb",
          "Mar",
          "Apr",
          "May",
          "Jun",
          "Jul",
          "Aug",
          "Sep",
          "Oct",
          "Nov",
          "Dec",
        ],
        datasets: {
          label: "Monthly Sales",
          data: allMonthsData,
        },
      });
    } catch (error) {
      console.error("Error fetching monthly chart data:", error);
    }
  };

  return (
    <DashboardLayout>
      <MDBox display="flex" justifyContent="flex-end" alignItems="center">
        <LogoutButton onLogout={() => navigate("/login")} />
      </MDBox>

      <DashboardNavbar />
      {(role === "Admin" || role === "Super Admin" || role === "Marketing Manager" || role === "Telecaller") && (
        <MDBox mb={3}>
          {" "}
          { }
          <DropDown onSelectOption={setSelectedEmail} />
        </MDBox>
      )}

      {role === "Admin" || role === "Vendor" || role === "Super Admin" || role === "Marketing Manager" || role === "Telecaller" ? (
        <MDBox py={3}>
          { }
          <Grid container spacing={3} mb={3}>
            {" "}
            { }
            <Grid item xs={12} md={6}>
              <Paper
                elevation={6}
                sx={{
                  p: 3,
                  borderRadius: theme.borders.borderRadius.xl,
                  backgroundColor: theme.palette.mode === "light" ? "#d7e6f9" : "#212121",
                  boxShadow: theme.shadows[6],
                }}
              >
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ mb: 4 }}
                  color="text.primary"
                >
                  Order Summary
                </MDTypography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} mb={2}>
                    {" "}
                    { }
                    <ComplexStatisticsCard
                      color="primary"
                      icon="store"
                      title="Total Orders"
                      count={dashboard.order_count}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} mb={2}>
                    <ComplexStatisticsCard
                      color="warning"
                      icon="access_time_filled"
                      title="Pending Orders"
                      count={dashboard.pending_count}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} mb={2}>
                    <ComplexStatisticsCard
                      color="success"
                      icon="done_outline"
                      title="Completed Orders"
                      count={dashboard.sales_count}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} mb={2}>
                    <ComplexStatisticsCard
                      color="error"
                      icon="cancel"
                      title="Cancelled Orders"
                      count={dashboard.reject_count}
                    />
                  </Grid>
                </Grid>
              </Paper>
            </Grid>

            { }
            <Grid item xs={12} md={6} >
              <Paper
                elevation={6}
                sx={{
                  p: 3,
                  borderRadius: theme.borders.borderRadius.xl,
                  backgroundColor: theme.palette.mode === "light" ? "#f9efd7" : "#212121",
                  boxShadow: theme.shadows[6],
                }}
              >
                <MDTypography
                  variant="h6"
                  fontWeight="bold"
                  gutterBottom
                  sx={{ mb: 4 }}
                  color="text.primary"
                >
                  Statistics Overview
                </MDTypography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} mb={2}>
                    <ComplexStatisticsCard
                      color="dark"
                      icon="trending_up"
                      title="Total Sales"
                      count={dashboard.sales_count}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} mb={2}>
                    <ComplexStatisticsCard
                      color="info"
                      icon="local_mall"
                      title="Total Products"
                      count={dashboard.product_count}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} mb={2}>
                    <ComplexStatisticsCard
                      color="success"
                      icon="people_alt"
                      title="Total Customers"
                      count={dashboard.customer_count}
                    />
                  </Grid>
                  {(role === "Admin" || role === "Super Admin" || role === "Marketing Manager" || role === "Telecaller") && (
                    <Grid item xs={12} sm={6} mb={2}>
                      <ComplexStatisticsCard
                        color="primary"
                        icon="store"
                        title="Total Vendors"
                        count={dashboard.vendor_count}
                      />
                    </Grid>
                  )}
                </Grid>
              </Paper>
            </Grid>
          </Grid>

          { }
          <Divider sx={{ mt: 4, mb: 4 }} /> { }
          <Paper
            elevation={6}
            sx={{
              mt: 2,
              p: 3,
              backgroundColor: theme.palette.background.paper,
              borderRadius: theme.borders.borderRadius.xl,
              boxShadow: theme.shadows[6],
            }}
          >
            <MDTypography
              variant="h6"
              fontWeight="bold"
              mb={6}
              gutterBottom
              color="text.primary"
            >
              Sales Overview
            </MDTypography>
            <Grid container spacing={5}>
              {" "}
              { }
              <Grid item xs={12} md={6} mb={2}>
                <ReportsBarChart
                  color="info"
                  title="Sales Today"
                  description="Total products sold today."
                  chart={
                    chartDataToday || { labels: [], datasets: [{ label: "Sales", data: [] }] }
                  }
                />
              </Grid>
              <Grid item xs={12} md={6} mb={2}>
                <ReportsLineChart
                  color="success"
                  title="Sales this week"
                  description="Products sold over the last 7 days."
                  chart={
                    chartDataWeek || { labels: [], datasets: [{ label: "Sales", data: [] }] }
                  }

                />
              </Grid>
              <Grid item xs={12} md={6} mb={2}>
                <ReportsLineChart
                  color="dark"
                  title="Sales this month"
                  description="Products sold in the current month."
                  chart={chartDataMonth}

                />
              </Grid>
              <Grid item xs={12} md={6} mb={2}>
                { }
                <ReportsLineChart
                  color="primary"
                  title="Sales last month"
                  description="Products sold in the previous month. "
                  chart={chartDataMonth}

                />
              </Grid>
            </Grid>
          </Paper>
        </MDBox>
      ) : (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="flex-start"
          sx={{
            minHeight: "80vh",
            pt: 6,
            pb: 3,
            px: 2,
          }}
        >
          <Paper
            elevation={10}
            sx={{
              maxWidth: 700,
              p: { xs: 2, sm: 4 },
              borderRadius: 3,
              backgroundColor: (theme) => theme.palette.background.paper,
              backdropFilter: "blur(10px)",
              border: (theme) => `1px solid ${theme.palette.divider}`,
              boxShadow: (theme) => theme.shadows[10],
              width: "100%",
              textAlign: "center",
              transition: "transform 0.3s ease-out, box-shadow 0.3s ease-out",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: (theme) => theme.shadows[12],
              },
              mb: 4,
            }}
          >
            <Box display="flex" flexDirection="column" alignItems="center" gap={1} mb={2}>
              <WavingHandIcon sx={{ color: yellow[700], fontSize: 36 }} />
              <Typography variant="h5" fontWeight={600} color="text.primary" sx={{ lineHeight: 1.2 }}>
                Welcome to Just Click India!
              </Typography>
            </Box>
            <Typography variant="body2" color="text.secondary" lineHeight={1.4} maxWidth={520} mx="auto" sx={{ fontSize: '0.875rem' }}>
              Just Click India is your one-stop destination for top-quality home and professional services, simplifying your daily needs with just a click.
              Whether you're a service provider or a customer, our platform connects you seamlessly.
            </Typography>

            <Divider
              sx={{ my: 4, mb: 6 }} // add mb or increase the value of my/mb
            >
              <Chip label="YOUR AVAILABILITY STATUS" color="primary" sx={{ fontWeight: "bold", fontSize: '0.75rem', px: 1.5, py: 0.3 }} />
            </Divider>

            <Box
              display="flex"
              flexDirection={{ xs: "column", sm: "row" }}
              alignItems="center"
              justifyContent="space-between"
              p={4}
              borderRadius={2}
              bgcolor={isAvailable ? green[600] : red[600]}
              color="white"
              sx={{ transition: "background-color 0.4s ease-in-out", boxShadow: 3, mb: 3 }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                {isAvailable ? (
                  <CheckCircleOutlineIcon sx={{ fontSize: 30 }} />
                ) : (
                  <HighlightOffIcon sx={{ fontSize: 30 }} />
                )}
                <Box textAlign={{ xs: "center", sm: "left" }}>
                  <Typography variant="subtitle1" fontWeight={600}>
                    Status: {isLoading ? "Loading..." : isAvailable ? "Available" : "Unavailable"}
                  </Typography>
                  <Typography variant="caption" sx={{ opacity: 0.85 }}>
                    Toggle this switch to set your availability for new service requests
                  </Typography>
                </Box>
              </Box>

              <FormControlLabel
                sx={{ mt: { xs: 2, sm: 0 }, ml: { sm: 2 } }}
                control={
                  <Switch
                    checked={isAvailable}
                    onChange={handleAvailabilityChange}
                    color="default"
                    disabled={isLoading}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': { color: '#fff' },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: 'rgba(255,255,255,0.5)',
                        opacity: 1,
                      },
                      '& .MuiSwitch-switchBase': { color: '#fff' },
                      '& .MuiSwitch-switchBase + .MuiSwitch-track': {
                        backgroundColor: 'rgba(255,255,255,0.3)',
                        opacity: 1,
                      },
                    }}
                  />
                }
                label={
                  <Typography variant="subtitle2" fontWeight={700} sx={{ color: 'white' }}>
                    {isLoading ? "Loading..." : isAvailable ? "Online" : "Offline"}
                  </Typography>
                }
              />
            </Box>
          </Paper>

          <Paper
            elevation={6}
            sx={{
              maxWidth: 700,
              p: { xs: 2, sm: 3 },
              borderRadius: 3,
              backgroundColor: (theme) => (theme.palette.mode === "light" ? "#f3faff" : "#272727"),
              boxShadow: 3,
              width: "100%",
            }}
          >
            <MDTypography
              variant="subtitle1"
              fontWeight="bold"
              mb={2}
              color="text.primary"
              sx={{ textAlign: "center", fontSize: "1rem" }}
            >
              Update Current Location
            </MDTypography>

            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={9}>
                <TextField
                  label="Current Location"
                  variant="outlined"
                  fullWidth
                  value={currentLocation}
                  placeholder="Location not set"
                  InputProps={{
                    readOnly: true,
                    sx: { cursor: "not-allowed", userSelect: "none", fontSize: "0.875rem" },
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="Fetch current location"
                          onClick={handleGetCurrentLocation}
                          edge="end"
                          disabled={isFetchingLocation}
                          size="large"
                        >
                          {isFetchingLocation ? <CircularProgress size={20} /> : <MyLocationIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ "& .MuiInputBase-input": { fontSize: "0.875rem", py: 0.75 } }}
                />
              </Grid>
            </Grid>
          </Paper>

        </Box>
      )}
    </DashboardLayout>
  );
}

export default Dashboard;